import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/EngagementTracking.graphql';
import { Query, QueryengagementTrackingArgs } from '../../__generated__/globalTypes';

export const useEngagementTrackingQuery = (options?: QueryHookOptions<Query, QueryengagementTrackingArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryengagementTrackingArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useEngagementTrackingLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryengagementTrackingArgs>
): QueryResult<Query, QueryengagementTrackingArgs> & {
  getEngagementTracking: LazyQueryExecFunction<Query, QueryengagementTrackingArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getEngagementTracking, result] = useLazyQuery<Query, QueryengagementTrackingArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getEngagementTracking, ...result };
};

export const useEngagementTrackingPromiseQuery = () => {
  const client = useApolloClient();

  const getEngagementTracking = useCallback(
    (options?: Omit<QueryOptions<QueryengagementTrackingArgs, Query>, 'query'>) =>
      client.query<Query, QueryengagementTrackingArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getEngagementTracking };
};
// Suspense Query Hooks
export const useEngagementTrackingSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryengagementTrackingArgs>
) => {
  const result = useSuspenseQuery<Query, QueryengagementTrackingArgs>(QUERY, {
    ...options
  });

  return result;
};
